import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './DateTimePickers.module.scss';

/**
 * Date / time picker popovers for the project board (QA fix run — restores
 * the legacy pickers the Backbone board attached on `focus .date-input` /
 * `focus .time-input`; see the port-decision comments in orderShared.tsx and
 * TimelineTab.tsx for the wiring history).
 *
 * DATE — jQuery-UI 1.12.1 datepicker structural equivalent. All three legacy
 * surfaces used the identical config (shipping_order.js:326-334,
 * service_order.js:394-402, timeline_item.js:58-63):
 *   { isLTR: true, dateFormat: 'yy-mm-dd', changeMonth: true, changeYear: true }
 * (`isLTR` is not a jQuery-UI option — ignored there, ignored here.) So:
 * prev/next month header, short-month + year dropdowns (the year list is the
 * one deliberate deviation — see YearSelect), Su-first
 * day-of-week row, day grid with today/selected highlighting. Picking WRITES
 * `yyyy-mm-dd` — jQuery-UI's `yy-mm-dd` output, dashes, exactly what the
 * legacy picker saved through the plain `change input` autosave (the
 * `yyyy.mm.dd` input placeholder never matched the picker output in legacy
 * either; validateDateTime only re-dotted the collapsed-summary spans).
 * The markup reuses the real jQuery-UI ids/classes because the route already
 * loads their theming: /static/css/project/styles.css `@import`s
 * /static/vendor/jquery/datepicker.css and styles.css:4471 themes
 * `#ui-datepicker-div` directly.
 *
 * TIME — ClockPicker v0.0.7 structural equivalent (shipping_order.js:343-355,
 * service_order.js:379-392):
 *   { placement: 'bottom', align: 'right', autoclose: true, default: 'now',
 *     donetext: 'Done' }
 * 24-hour (twelvehour defaults false), hours dial (1-12 inner ring, 13-00
 * outer) then minutes dial (00..55 by 5); autoclose ⇒ no Done button — the
 * minute click commits. done() writes zero-padded `HH:MM` (clockpicker.min.js
 * done(): `i(this.hours)+":"+i(this.minutes)`) — again the placeholder
 * (`hh.mm`) never matched the picker output in legacy. `default: 'now'` seeds
 * the dial with the current time when the input is empty. Bootstrap's
 * `.popover` chrome comes from the already-loaded bootstrap.min.css (another
 * styles.css @import); the clockpicker-specific plate/dial/tick CSS was NEVER
 * loaded on the legacy project board (styles.css has zero `.clockpicker`
 * rules), so DateTimePickers.module.scss carries a minimal copy of the vendor
 * rules (query-clockpicker.min.css) — documented improvement over the
 * unstyled dial legacy users saw.
 *
 * Both popovers portal to document.body (legacy appended there too), close on
 * outside mousedown, outside focusin (tab-away — clockpicker show() bound
 * focusin; jQuery-UI hides on TAB) and Escape (both legacy widgets), and
 * commit through the SAME onPick → save path the typed input uses.
 */

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

// ---------------------------------------------------------------------------
// Shared popover plumbing
// ---------------------------------------------------------------------------

/** Anchor rect in page coordinates, measured once when the popover opens. */
const usePageRect = (anchor: HTMLElement) =>
  useState(() => {
    const r = anchor.getBoundingClientRect();
    return {
      top: r.top + window.scrollY,
      bottom: r.bottom + window.scrollY,
      left: r.left + window.scrollX,
      right: r.right + window.scrollX,
    };
  })[0];

/** Outside-mousedown / outside-focusin / Escape → onClose. */
const useDismiss = (
  popRef: React.RefObject<HTMLDivElement | null>,
  anchor: HTMLElement,
  onClose: () => void
) => {
  useEffect(() => {
    const isInside = (t: EventTarget | null) =>
      t instanceof Node && (popRef.current?.contains(t) || anchor.contains(t));
    const onDown = (e: MouseEvent) => {
      if (!isInside(e.target)) onClose();
    };
    const onFocusIn = (e: FocusEvent) => {
      if (!isInside(e.target)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('keydown', onKey);
    };
  }, [popRef, anchor, onClose]);
};

// ---------------------------------------------------------------------------
// Date picker (jQuery-UI datepicker equivalent)
// ---------------------------------------------------------------------------

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const DAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];
const DAY_NAMES_MIN = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** Lenient `yyyy-mm-dd` / `yyyy.mm.dd` parse of the typed/saved value. */
const parseDateValue = (
  value: string
): { y: number; m0: number; d: number } | null => {
  const m = /^\s*(\d{4})[-./](\d{1,2})[-./](\d{1,2})\s*$/.exec(value);
  if (!m) return null;
  const y = Number(m[1]);
  const m0 = Number(m[2]) - 1;
  const d = Number(m[3]);
  const probe = new Date(y, m0, d);
  if (probe.getFullYear() !== y || probe.getMonth() !== m0 || probe.getDate() !== d) {
    return null;
  }
  return { y, m0, d };
};

// ---------------------------------------------------------------------------
// Year control
// ---------------------------------------------------------------------------
//
// jQuery-UI's default yearRange is 'c-10:c+10' recentred on the DRAWN year, and
// the first port copied it. That window is a trap: pick 2006 and the list
// becomes 1996-2016, so the current year is no longer reachable and every extra
// decade backwards costs another open/pick/reopen round. Requested change
// (QA, 2026-09-09 — NOT a legacy parity fix, legacy behaves the same way):
// the list always runs up to the current year, and scrolling up past the top
// loads earlier years so there is no fixed lower bound.
//
// A native <select> popup is drawn by the OS and fires no scroll event we can
// hook, so the popup is suppressed (mousedown/keydown preventDefault) and an
// own listbox is drawn under the real <select>. The <select> itself stays in
// the DOM, keeping `.ui-datepicker .ui-datepicker-title select` /
// `select.ui-datepicker-year` theming (datepicker.css:77-84) and a working
// fallback if a browser ignores the preventDefault.

/** Years appended per upward scroll, and how far back the list may ever go. */
const YEAR_CHUNK = 20;
const EARLIEST_YEAR = 1000; // 4-digit floor — parseDateValue only reads \d{4}.
/** Distance from the top of the list that triggers the next chunk. */
const SCROLL_TRIGGER_PX = 24;

const YearSelect = ({
  viewYear,
  onChange,
}: {
  viewYear: number;
  onChange: (year: number) => void;
}) => {
  const currentYear = new Date().getFullYear();
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [floor, setFloor] = useState(() => Math.min(viewYear, currentYear) - 10);

  // The drawn year always sits inside the list even when it was set from the
  // typed value or the month arrows; the top always reaches the current year
  // (plus jQuery-UI's +10 of future headroom — shipping dates are scheduled
  // ahead, so the future half of the legacy window is kept).
  const lowYear = Math.max(EARLIEST_YEAR, Math.min(floor, viewYear - 10));
  const highYear = Math.max(currentYear, viewYear) + 10;
  const years: number[] = [];
  for (let y = lowYear; y <= highYear; y += 1) years.push(y);

  // The list is a child of #ui-datepicker-div (which is position:absolute, so
  // it is the containing block) rather than its own body portal: a portal
  // would sit OUTSIDE the popover, and the popover's outside-mousedown dismiss
  // would tear the whole calendar down on the click that picks a year.
  // Offsets are measured against that same offsetParent when the list opens.
  const [listPos, setListPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const openList = () => {
    const sel = selectRef.current;
    if (sel) {
      setListPos({
        top: sel.offsetTop + sel.offsetHeight,
        left: sel.offsetLeft,
        width: sel.offsetWidth,
      });
    }
    setOpen(true);
  };

  // Close on a mousedown anywhere else in the calendar (clicks outside the
  // calendar are already the popover's own dismiss, which unmounts us).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target;
      if (t instanceof Node && !listRef.current?.contains(t) && t !== selectRef.current) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the selected year visible when the list opens.
  useLayoutEffect(() => {
    if (!open) return;
    const list = listRef.current;
    const active = activeRef.current;
    if (list && active) {
      list.scrollTop = active.offsetTop - (list.clientHeight - active.offsetHeight) / 2;
    }
    // Deliberately keyed on `open` alone — once it is up, the scroll position
    // belongs to the user.
  }, [open]);

  // Prepending earlier years shifts everything down; re-add the height that
  // appeared above the viewport so the list stays put under the cursor.
  const growthAnchor = useRef<number | null>(null);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list && growthAnchor.current !== null) {
      list.scrollTop += list.scrollHeight - growthAnchor.current;
      growthAnchor.current = null;
    }
  });

  const loadEarlier = () => {
    if (lowYear <= EARLIEST_YEAR) return;
    growthAnchor.current = listRef.current?.scrollHeight ?? null;
    setFloor(Math.max(EARLIEST_YEAR, lowYear - YEAR_CHUNK));
  };

  const onListScroll = () => {
    const list = listRef.current;
    if (list && list.scrollTop <= SCROLL_TRIGGER_PX) loadEarlier();
  };

  const pick = (year: number) => {
    onChange(year);
    setOpen(false);
    selectRef.current?.focus();
  };

  const step = (delta: number) => {
    const next = viewYear + delta;
    if (next < EARLIEST_YEAR || next > highYear) return;
    // Stepping below the loaded floor pulls the floor down with it.
    if (next - 10 < lowYear) setFloor(Math.max(EARLIEST_YEAR, next - 10));
    onChange(next);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLSelectElement>) => {
    // Never let the native popup open — it cannot grow on scroll.
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      step(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (e.altKey) openList();
      else step(1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (open) setOpen(false);
      else openList();
    }
  };

  return (
    <>
      <select
        ref={selectRef}
        className="ui-datepicker-year"
        title="Change the year"
        value={viewYear}
        onMouseDown={(e) => {
          e.preventDefault(); // suppress the native popup
          if (open) setOpen(false);
          else openList();
        }}
        onKeyDown={onKeyDown}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
      {open && listPos && (
        <div
          ref={listRef}
          className="ui-datepicker-year-list"
          role="listbox"
          onScroll={onListScroll}
          style={{
            position: 'absolute',
            top: listPos.top,
            left: listPos.left,
            // +14 so the year text is not squeezed by the scrollbar the
            // select itself never had to make room for.
            minWidth: listPos.width + 14,
            zIndex: 2,
          }}
        >
          {/* `is-selected` rather than jQuery-UI's `ui-state-active`: that
              class carries a 1px border (datepicker.css:201) which would nudge
              whichever row it lands on. */}
          {years.map((y) => (
            <div
              key={y}
              ref={y === viewYear ? activeRef : undefined}
              role="option"
              aria-selected={y === viewYear}
              className={`ui-datepicker-year-option${y === viewYear ? ' is-selected' : ''}`}
              onClick={() => pick(y)}
            >
              {y}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export const DatePickerPopover = ({
  anchor,
  value,
  minDate,
  maxDate,
  onPick,
  onClose,
}: {
  anchor: HTMLInputElement;
  value: string;
  /**
   * jQuery-UI minDate/maxDate equivalents (sync K, timeline_item.js
   * changeDateInput: the sched-date pickers bound each other) — canonical
   * `yyyy-mm-dd`; days outside the range render as jQuery-UI's unselectable
   * cells (`td.ui-datepicker-unselectable.ui-state-disabled >
   * span.ui-state-default`), styled by the already-loaded
   * /static/css/project/styles.css:4502 rule (dev's _shipping_service.scss
   * addition — #f5f5f5 / #ccc / cursor default). Unparseable → no bound.
   */
  minDate?: string;
  maxDate?: string;
  /** Receives jQuery-UI `yy-mm-dd` output, e.g. "2026-07-15". */
  onPick: (value: string) => void;
  onClose: () => void;
}) => {
  const rect = usePageRect(anchor);
  const popRef = useRef<HTMLDivElement | null>(null);
  useDismiss(popRef, anchor, onClose);

  const today = new Date();
  const selected = useMemo(() => parseDateValue(value), [value]);
  const minParsed = useMemo(() => (minDate ? parseDateValue(minDate) : null), [minDate]);
  const maxParsed = useMemo(() => (maxDate ? parseDateValue(maxDate) : null), [maxDate]);
  const minTime = minParsed ? new Date(minParsed.y, minParsed.m0, minParsed.d).getTime() : null;
  const maxTime = maxParsed ? new Date(maxParsed.y, maxParsed.m0, maxParsed.d).getTime() : null;
  const [viewYear, setViewYear] = useState(selected?.y ?? today.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected?.m0 ?? today.getMonth());
  const isDisabledDay = (d: number) => {
    const t = new Date(viewYear, viewMonth, d).getTime();
    return (minTime !== null && t < minTime) || (maxTime !== null && t > maxTime);
  };

  const stepMonth = (delta: number) => {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  // Day grid: firstDay 0 (Su), other-month cells empty (showOtherMonths off).
  const leading = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const rowCount = Math.ceil((leading + daysInMonth) / 7);
  const cells: (number | null)[] = [];
  for (let i = 0; i < rowCount * 7; i += 1) {
    const day = i - leading + 1;
    cells.push(day >= 1 && day <= daysInMonth ? day : null);
  }
  const rows: (number | null)[][] = [];
  for (let r = 0; r < rowCount; r += 1) rows.push(cells.slice(r * 7, r * 7 + 7));

  const isToday = (d: number) =>
    viewYear === today.getFullYear() &&
    viewMonth === today.getMonth() &&
    d === today.getDate();
  const isSelected = (d: number) =>
    !!selected && selected.y === viewYear && selected.m0 === viewMonth && selected.d === d;

  return createPortal(
    <div
      ref={popRef}
      id="ui-datepicker-div"
      className="ui-datepicker ui-widget ui-widget-content ui-helper-clearfix ui-corner-all"
      style={{
        position: 'absolute',
        top: rect.bottom,
        left: rect.left,
        zIndex: 1010,
        display: 'block',
      }}
    >
      <div className="ui-datepicker-header ui-widget-header ui-helper-clearfix ui-corner-all">
        <a
          className="ui-datepicker-prev ui-corner-all"
          data-handler="prev"
          title="Prev"
          onClick={() => stepMonth(-1)}
        >
          <span className="ui-icon ui-icon-circle-triangle-w">Prev</span>
        </a>
        <a
          className="ui-datepicker-next ui-corner-all"
          data-handler="next"
          title="Next"
          onClick={() => stepMonth(1)}
        >
          <span className="ui-icon ui-icon-circle-triangle-e">Next</span>
        </a>
        <div className="ui-datepicker-title">
          <select
            className="ui-datepicker-month"
            title="Change the month"
            value={viewMonth}
            onChange={(e) => setViewMonth(Number(e.target.value))}
          >
            {MONTHS_SHORT.map((label, m0) => (
              <option key={label} value={m0}>
                {label}
              </option>
            ))}
          </select>
          <YearSelect viewYear={viewYear} onChange={setViewYear} />
        </div>
      </div>
      <table className="ui-datepicker-calendar">
        <thead>
          <tr>
            {DAY_NAMES_MIN.map((min, i) => (
              <th key={min} scope="col" className={i === 0 || i === 6 ? 'ui-datepicker-week-end' : ''}>
                <span title={DAY_NAMES[i]}>{min}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r}>
              {row.map((day, c) => {
                const weekEnd = c === 0 || c === 6 ? ' ui-datepicker-week-end' : '';
                if (day === null) {
                  return (
                    <td
                      key={c}
                      className={`${weekEnd} ui-datepicker-other-month ui-datepicker-unselectable ui-state-disabled`}
                    >
                      {' '}
                    </td>
                  );
                }
                if (isDisabledDay(day)) {
                  // jQuery-UI's out-of-range day: unselectable td, day number
                  // in a plain span (no anchor → no hover rule, no handler).
                  return (
                    <td
                      key={c}
                      className={`${weekEnd}${
                        isToday(day) ? ' ui-datepicker-today' : ''
                      } ui-datepicker-unselectable ui-state-disabled`}
                    >
                      <span className="ui-state-default">{day}</span>
                    </td>
                  );
                }
                return (
                  <td
                    key={c}
                    className={`${weekEnd}${isToday(day) ? ' ui-datepicker-today' : ''}${
                      isSelected(day) ? ' ui-datepicker-current-day' : ''
                    }`}
                    data-handler="selectDay"
                  >
                    <a
                      className={`ui-state-default${isToday(day) ? ' ui-state-highlight' : ''}${
                        isSelected(day) ? ' ui-state-active' : ''
                      }`}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onPick(`${viewYear}-${pad2(viewMonth + 1)}-${pad2(day)}`);
                      }}
                    >
                      {day}
                    </a>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// Time picker (ClockPicker v0.0.7 equivalent)
// ---------------------------------------------------------------------------

// ClockPicker geometry constants (clockpicker.min.js defaults).
const DIAL_RADIUS = 100;
const OUTER_RADIUS = 80;
const INNER_RADIUS = 54;
const TICK_RADIUS = 13;
// Plate 200px + popover-content padding 12px×2 + popover border 1px×2.
const POPOVER_WIDTH = 226;

const tickPos = (index: number, radius: number) => {
  const radian = (index / 6) * Math.PI;
  return {
    left: DIAL_RADIUS + Math.sin(radian) * radius - TICK_RADIUS,
    top: DIAL_RADIUS - Math.cos(radian) * radius - TICK_RADIUS,
  };
};

/** show(): (value || default 'now').split(':') — we also accept '.' typed. */
const parseTimeValue = (value: string): { h: number; m: number } => {
  const m = /^\s*(\d{1,2})[:.](\d{1,2})/.exec(value);
  if (m) return { h: Number(m[1]) % 24, m: Number(m[2]) % 60 };
  const now = new Date();
  return { h: now.getHours(), m: now.getMinutes() };
};

export const TimePickerPopover = ({
  anchor,
  value,
  onPick,
  onClose,
}: {
  anchor: HTMLInputElement;
  value: string;
  /** Receives clockpicker done() output, zero-padded `HH:MM` (24h). */
  onPick: (value: string) => void;
  onClose: () => void;
}) => {
  const rect = usePageRect(anchor);
  const popRef = useRef<HTMLDivElement | null>(null);
  useDismiss(popRef, anchor, onClose);

  const [{ h: hours, m: minutes }, setTime] = useState(() => parseTimeValue(value));
  const [view, setView] = useState<'hours' | 'minutes'>('hours');

  // Hours dial: 1-12 on the inner ring (120% font), 13-23 + 00 outer.
  const hourTicks = [];
  for (let i = 0; i < 24; i += 1) {
    const inner = i > 0 && i < 13;
    hourTicks.push({
      value: i,
      label: i === 0 ? '00' : String(i),
      inner,
      ...tickPos(i, inner ? INNER_RADIUS : OUTER_RADIUS),
    });
  }
  // Minutes dial: 00..55 by 5, outer ring, 120% font.
  const minuteTicks = [];
  for (let i = 0; i < 60; i += 5) {
    minuteTicks.push({ value: i, label: pad2(i), ...tickPos(i / 5, OUTER_RADIUS) });
  }

  const pickHour = (h: number) => {
    setTime((t) => ({ ...t, h }));
    setView('minutes'); // toggleView('minutes') after an hour click
  };
  const pickMinute = (m: number) => {
    // autoclose: the minute click runs done() — write HH:MM and dismiss.
    onPick(`${pad2(hours)}:${pad2(m)}`);
  };

  return createPortal(
    // placement 'bottom' + align 'right': below the input, right edges flush.
    <div
      ref={popRef}
      className="popover clockpicker-popover bottom clockpicker-align-right"
      style={{
        display: 'block',
        top: rect.bottom,
        left: rect.right - POPOVER_WIDTH,
        width: POPOVER_WIDTH,
      }}
    >
      <div className="arrow" />
      <div className="popover-title">
        <span
          className={`clockpicker-span-hours${view === 'hours' ? ' text-primary' : ''}`}
          onClick={() => setView('hours')}
        >
          {pad2(hours)}
        </span>
        {' : '}
        <span
          className={`clockpicker-span-minutes${view === 'minutes' ? ' text-primary' : ''}`}
          onClick={() => setView('minutes')}
        >
          {pad2(minutes)}
        </span>
      </div>
      <div className="popover-content">
        <div className="clockpicker-plate">
          {/* Dial switching uses display, not the vendor's visibility toggle:
              common.css ships `body :not(header){visibility:visible}` (anti-
              FOUC hack, loaded app-wide), which force-overrides visibility on
              every descendant — a visibility-hidden dial keeps PAINTING its
              ticks and INTERCEPTING clicks (the minute ticks sit exactly on
              the outer hour ring). display:none is immune to that override.
              (Same reason the vendor sheet's own `.clockpicker-minutes
              {visibility:hidden}` can't be relied on here.) */}
          <div
            className="clockpicker-dial clockpicker-hours"
            style={{
              display: view === 'hours' ? 'block' : 'none',
              visibility: view === 'hours' ? 'visible' : 'hidden',
            }}
          >
            {hourTicks.map((t) => (
              <div
                key={t.value}
                className={`clockpicker-tick${t.value === hours ? ' active' : ''}`}
                style={{
                  left: t.left,
                  top: t.top,
                  fontSize: t.inner ? '120%' : undefined,
                }}
                onClick={() => pickHour(t.value)}
              >
                {t.label}
              </div>
            ))}
          </div>
          <div
            className="clockpicker-dial clockpicker-minutes"
            style={{
              display: view === 'minutes' ? 'block' : 'none',
              visibility: view === 'minutes' ? 'visible' : 'hidden',
            }}
          >
            {minuteTicks.map((t) => (
              <div
                key={t.value}
                className={`clockpicker-tick${t.value === minutes ? ' active' : ''}`}
                style={{ left: t.left, top: t.top, fontSize: '120%' }}
                onClick={() => pickMinute(t.value)}
              >
                {t.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
